import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, CircularProgress, Stack, Typography } from '@mui/material';
import { saveAs } from 'file-saver';
import { ScribeButton as Button } from '../../components/scribe/ScribeUI';
import { getConsentDocuments, downloadConsentEvidence } from '../../services/clientConsentService';
import { CONSENT_CHANGED, consentDate } from './ClientConsentPanel';
export default function ConsentDocuments({
  patientId
}) {
  const [files, setFiles] = useState([]),
    [loading, setLoading] = useState(false),
    [error, setError] = useState(''),
    [busy, setBusy] = useState('');
  const generation = useRef(0);
  const load = useCallback(async () => {
    const version = ++generation.current;
    setFiles([]);
    setLoading(true);
    setError('');
    try {
      const result = await getConsentDocuments(patientId);
      if (version === generation.current) setFiles(result);
    } catch (e) {
      if (version === generation.current) setError(e.message);
    } finally {
      if (version === generation.current) setLoading(false);
    }
  }, [patientId]);
  useEffect(() => {
    if (patientId) void load();
    return () => {
      generation.current += 1;
    };
  }, [patientId, load]);
  useEffect(() => {
    const changed = event => {
      if (event.detail?.patientId === patientId) void load();
    };
    const focus = () => {
      if (patientId) void load();
    };
    window.addEventListener(CONSENT_CHANGED, changed);
    window.addEventListener('focus', focus);
    return () => {
      window.removeEventListener(CONSENT_CHANGED, changed);
      window.removeEventListener('focus', focus);
    };
  }, [patientId, load]);
  const download = async file => {
    const version = generation.current;
    setBusy(file.path);
    setError('');
    try {
      const result = await downloadConsentEvidence(patientId, file);
      if (version === generation.current) saveAs(result.blob, result.name);
    } catch (e) {
      if (version === generation.current) setError(e.message);
    } finally {
      setBusy('');
    }
  };
  if (!patientId || (!loading && !error && !files.length)) return null;
  return <Box component="section" aria-label="Consent documents" sx={{
    bgcolor: '#fff',
    border: '1px solid #E5E7EB',
    borderRadius: 2,
    p: 2.5,
    mb: 2
  }}>
  <Typography sx={{
      fontSize: 14,
      fontWeight: 600,
      mb: 1
    }}>Consent documents</Typography>
  <Typography variant="body2" color="text.secondary" sx={{
      mb: 1
    }}>Documents attached to written consent. Stored once and available here and in the consent log.</Typography>
  {loading ? <CircularProgress size={20} aria-label="Loading consent documents" /> : error ? <Alert severity="error" action={<Button onClick={load}>Retry</Button>}>{error}</Alert> : <Stack spacing={1}>{files.map(file => <Stack key={file.path} direction="row" justifyContent="space-between" alignItems="center" gap={2} sx={{
        border: '1px solid #E5E7EB',
        borderRadius: 1,
        p: 1.5
      }}>
   <Box sx={{
          minWidth: 0
        }}><Typography variant="body2" sx={{
            overflowWrap: 'anywhere'
          }}>{file.name}</Typography><Typography variant="caption" color="text.secondary">Written consent · {consentDate(file.uploaded_at)}</Typography></Box>
   <Button size="small" disabled={busy === file.path} onClick={() => download(file)}>Download</Button>
  </Stack>)}</Stack>}
 </Box>;
}
